<template>
  <v-card
    id="playposit-rubric"
    class="mb-3"
    :class="{'disabled': disabled}"
    flat
    tile
    color="#FAFAFA"
  >
    <v-snackbar
      v-model="snackbar.enabled"
      :timeout="3000"
      :color="snackbar.color"
      top
      left
    >
      {{ snackbar.text }}
    </v-snackbar>
    <div
      class="rubric-loading-screen"
      v-if="!loaded"
    >
      <v-progress-circular
        indeterminate
        :color="activeHex"
      ></v-progress-circular>
    </div>
    <v-card-title
      ref="rubricHeader"
      class="rubric-header"
      :style="{
        'backgroundColor': activeHex
      }"
    >
      <v-layout class="relative" wrap justify-flex-start align-center>
        <v-flex xs4>
          <v-layout justify-flex-start align-center>
            <v-progress-circular
              v-show="processingRequest"
              indeterminate
              color="white"
            ></v-progress-circular>
            <v-tooltip right>
              <template>
                <v-icon
                  slot="activator"
                  v-show="rubricValidityIssues.length"
                  color="white"
                >
                  warning
                </v-icon>
              </template>
              <div>
                <ul class="mb-0" v-if="rubricValidityIssues.includes('missing-text') && rubricValidityIssues.includes('missing-criteria')">
                  <li>At least one numeric criterion is required.</li>
                  <li>The title of a section or criterion cannot be empty.</li>
                </ul>
                <span class="white--text" v-else-if="rubricValidityIssues.includes('missing-criteria')">
                  At least one numeric criterion is required.
                </span>
                <span class="white--text" v-else>
                  The title of a section or criterion cannot be empty.
                </span>
              </div>
            </v-tooltip>
          </v-layout>
        </v-flex>
        <v-flex xs4 class="text-xs-center full-height">
          <div class="white--text headline" v-show="title !== null">{{title}}</div>
          <div
            :class="{
              'body-1 text--faded-light': title !== null,
              'title font-weight-regular white--text': title === null
            }"
          >
            {{totalPoints}} points
          </div>
        </v-flex>
      </v-layout>
    </v-card-title>
    <v-layout class="grey lighten-3">
      <v-btn
        block
        flat
        color="primary"
        class="pa-0 ma-0 br-0"
        @click="addInteraction('pause_q')"
        :disabled="disabled"
      >
        <v-icon left>bookmark</v-icon>
        Add section
      </v-btn>
      <v-btn
        block
        flat
        color="primary"
        class="pa-0 ma-0 br-0"
        @click="addInteraction('short_answer')"
        :disabled="disabled"
      >
        <v-icon left>linear_scale</v-icon>
        Add numeric criterion
      </v-btn>
    </v-layout>
    <v-container
      ref="rubricBody"
      class="rubric-body"
      px-0
      py-3
    >
      <div
        v-show="!editorFriendlyInteractions.length"
        class="grey--text subheading text-xs-center"
      >
        Add rubric sections/criteria using the buttons below.
      </div>
      <v-form
        v-model="isFormValid"
        ref="rubricForm"
      >
        <v-layout
          class="rubric-criteria"
          justify-center
          align-center
          column
        >
          <draggable
            class="width-100"
            v-model="editorFriendlyInteractions"
            v-bind="draggableOptions"
            @end="applyInteractionBankOrder"
          >
            <template v-for="(interaction, interactionIndex) in editorFriendlyInteractions">
              <v-layout
                :key="interaction.id"
                :id="`rubric-${interactionIndex}`"
                v-if="interaction.interactionType === 'pause_q'"
                class="rubric-element section"
                :class="[`section-id-${interaction.id}`, {'has-criteria': interaction.criteria && interaction.criteria.length, 'is-focused': interaction.focused}]"
                :style="{'borderColor': activeHex}"
                column
              >
                <v-flex
                  class="section-title width-100"
                  xs12
                >
                  <v-layout
                    class="relative"
                    align-center
                    justify-space-between
                  >
                    <div class="side-actions">
                      <div class="drag-handle flex-all-center">
                        <v-icon
                          color="gray"
                          small
                        >drag_indicator</v-icon>
                      </div>
                    </div>
                    <v-text-field
                      flat
                      v-model="interaction.interactionText"
                      placeholder="Section"
                      :rules="[rules.notEmpty]"
                      :error="!interaction.interactionText.length"
                      prepend-inner-icon="bookmark"
                      type="text"
                      class="pt-0 mt-0"
                      hide-details
                      :disabled="disabled"
                    >
                    </v-text-field>
                    <v-btn
                      icon
                      @click="deleteInteraction(interactionIndex)"
                      :disabled="processingRequest"
                    >
                      <v-icon color="error">delete</v-icon>
                    </v-btn>
                  </v-layout>
                </v-flex>
              </v-layout>
              <v-layout
                v-else
                align-center
                justify-space-between
                :key="interaction.id"
                :id="`rubric-${interactionIndex}`"
                class="rubric-element criteria mb-2"
              >
                <div class="side-actions">
                  <div class="drag-handle flex-all-center">
                    <v-icon
                      color="gray"
                      small
                    >drag_indicator</v-icon>
                  </div>
                </div>
                <v-flex class="description pl-0 pr-3">
                  <v-textarea
                    lazy
                    class="pt-0 mt-0"
                    v-model="interaction.interactionText"
                    placeholder="Criteria"
                    :rules="[rules.notEmpty]"
                    :error="!interaction.interactionText.length"
                    prepend-inner-icon="linear_scale"
                    auto-grow
                    rows="1"
                    hide-details
                    :disabled="disabled"
                  ></v-textarea>
                </v-flex>
                <v-menu
                  class="min-w-150px"
                  :disabled="disabled"
                  content-class="rubric-slider-menu"
                  offset-y
                >
                  <template>
                    <v-btn
                      slot="activator"
                      outline
                      block
                      color="primary"
                      :disabled="disabled"
                    >
                      Out of {{interaction.pointsAvailable}} {{interaction.pointsAvailable === 1 ? 'point' : 'points'}}
                    </v-btn>
                  </template>
                  <v-list>
                    <v-list-tile class="height-auto">
                      <v-slider
                        min="1"
                        max="5"
                        ticks
                        :tick-labels="[1 , 2 , 3, 4 , 5]"
                        :value="interaction.pointsAvailable"
                        @change="$set(interaction, 'pointsAvailable', $event)"
                        :disabled="disabled"
                      ></v-slider>
                    </v-list-tile>
                  </v-list>
                </v-menu>
                <v-btn
                  icon
                  @click="deleteInteraction(interactionIndex)"
                  :disable="processingRequest"
                >
                  <v-icon color="error">delete</v-icon>
                </v-btn>
              </v-layout>
            </template>
          </draggable>
        </v-layout>
      </v-form>
    </v-container>
    <v-layout>
      <v-spacer></v-spacer>
      <v-btn
        :color="activeHex"
        :disabled="disabled || rubricValidityIssues.length > 0"
        @click="saveRubric"
      >
        <span v-if="buttonAction === 'next' && lti" class="white--text">Assign</span>
        <span v-else class="white--text">Save and Close</span>
      </v-btn>
    </v-layout>
  </v-card>
</template>

<script>
import draggable from 'vuedraggable';
import axios from 'axios';
import _ from 'lodash';

export default {
  name: 'playposit-rubric',
  components: {
    draggable,
  },
  props: {
    bulbId: {
      type: Number,
      required: true,
    },
    token: {
      type: String,
      required: true,
    },
    apiUrl: {
      type: String,
      default: '/',
    },
    saveToggle: {
      type: Boolean,
      default: false,
    },
    startTime: {
      type: Number,
      default: 0,
    },
    title: {
      type: String,
      default: null,
    },
    lti: {
      type: Boolean,
      default: false,
    },
    buttonAction: {
      type: String,
      default: null,
    },
    activeHex: {
      type: String,
      default: '#2196F3',
      validator: (value) => typeof value === 'string' && value.match(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/),
    },
    inactiveHex: {
      type: String,
      default: '#FFF',
      validator: (value) => typeof value === 'string' && value.match(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/),
    },
    baseHex: {
      type: String,
      default: '#1976D2',
      validator: (value) => typeof value === 'string' && value.match(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/),
    },
  },
  data: () => ({
    loaded: false,
    processingRequest: false,
    isFormValid: true,
    editorFriendlyInteractions: [],
    bulb: {
      interactions: [],
    },
    snackbar: {
      enabled: false,
      text: '',
      color: null,
    },
    rules: {
      notEmpty: (value) => !!value.length || 'This value cannot be empty.',
    },
  }),
  computed: {
    api() {
      return {
        applyInteractionBank: () => {
          const bank = this.bulb.banks[0];
          bank.interactions = this.databaseFriendlyInteractions;
          return axios.post(`${this.url}bank/apply`, {
            bank,
            isRubric: true,
          });
        },
        getBulbData: () => axios.get(`${this.url}designer/bulbdata/${this.bulbId}/rubric-no-discussion`),
        init: () => {
          axios.defaults.headers.common.Authorization = `Bearer ${this.token}`;
          axios.defaults.headers.common['Content-Type'] = 'application/x-www-form-urlencoded';
          axios.defaults.headers.common.Accept = 'application/json';
        },
      };
    },
    rubricValidityIssues() {
      const arr = [];
      if (!this.isFormValid) arr.push('missing-text');
      if (!this.editorFriendlyInteractions.filter((rubricInteraction) => rubricInteraction.interactionType === 'short_answer').length) {
        arr.push('missing-criteria');
      }
      return arr;
    },
    databaseFriendlyInteractions() {
      return this.editorFriendlyInteractions.map((editorFriendlyInteraction) => {
        if (editorFriendlyInteraction.interactionType === 'pause_q') {
          return _.merge({}, this.interactionTemplate, {
            id: editorFriendlyInteraction.id,
            interactionText: editorFriendlyInteraction.interactionText,
            interactionType: editorFriendlyInteraction.interactionType,
            displayInTime: editorFriendlyInteraction.displayInTime,
            pointsAvailable: editorFriendlyInteraction.pointsAvailable,
            interactionBank: editorFriendlyInteraction.interactionBank,
          });
        }
        return _.merge({}, this.interactionTemplate, {
          id: editorFriendlyInteraction.id,
          pointsAvailable: editorFriendlyInteraction.pointsAvailable,
          interactionType: editorFriendlyInteraction.interactionType,
          displayInTime: parseFloat(editorFriendlyInteraction.displayInTime),
          interactionText: (() => `<div class="pp-rubric-criteria"><div class="pp-rubric-criteria-text">${editorFriendlyInteraction.interactionText}</div><div class="pp-rubric-criteria-input"><input id="question-${editorFriendlyInteraction.id}-0" onkeydown="if(event.key==='.'){event.preventDefault();}" class="fill-blank-question" tabindex="-1" type="number" min="0" max="${editorFriendlyInteraction.pointsAvailable}" placeholder="0 - ${editorFriendlyInteraction.pointsAvailable}" /></div></div>`)(),
          interactionBank: editorFriendlyInteraction.interactionBank,
        });
      });
    },
    url() {
      return this.apiUrl[this.apiUrl.length - 1] === '/' ? this.apiUrl : `${this.apiUrl}/`;
    },
    interactionTemplate() {
      return {
        config: {
          template_key: 'rubric',
          numeric_only: 1,
          response_is_score: 1,
          answer_explanation: 0,
          mark_correct: 0,
          lmb_master: this.bulb.lmbId || 1,
        },
        bulbId: this.bulbId,
        lessonId: this.bulbId,
        isRequired: 1,
        retake: 0,
        rewind: 1,
        pause: 0,
        random: 0,
        position: {
          position_type: 'sidebar',
        },
        interactionOptions: [],
      };
    },
    disabled() {
      return !this.loaded || this.processingRequest;
    },
    totalPoints() {
      try {
        if (!_.get(this, 'editorFriendlyInteractions', []).length) return 0;
        return this.editorFriendlyInteractions
          .filter((editorFriendlyInteraction) => _.get(editorFriendlyInteraction, 'interactionType') === 'short_answer')
          .map((editorFriendlyInteraction) => _.get(editorFriendlyInteraction, 'pointsAvailable', 0))
          .reduce((a, b) => a + b, 0);
      } catch(e) {
        return 0;
      }
    },
    draggableOptions() {
      return {
        group: 'rubric',
        draggable: '.rubric-element',
        handle: '.drag-handle',
        disabled: this.disabled,
      };
    },
  },
  methods: {
    generateUuid() {
      return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    },
    async applyInteractionBankOrder() {
      for (const [index, interaction] of this.editorFriendlyInteractions.entries()) {
        // The rubric discussion needs to have an order of 0, so sections/criteria must start at 1.
        this.$set(interaction.interactionBank, 'order', index + 1);
      }
    },
    updatePointsAvailable(interactionId, pointsAvailable) {
      const foundInteraction = this.bulb.interactions.find((interaction) => interaction.id === interactionId);
      if (!foundInteraction) return false;
      this.$set(foundInteraction, 'pointsAvailable', pointsAvailable);
      return foundInteraction;
    },
    async addInteraction(type) {
      try {
        const template = _.merge({}, this.interactionTemplate, {
          interactionType: type,
          interactionText: (() => {
            if (type === 'pause_q') {
              return `Section #${this.editorFriendlyInteractions.filter((d) => d.interactionType !== 'short_answer').length + 1}`;
            } else if (type === 'short_answer') {
              const html = `
                <div class="pp-rubric-criteria">
                  <div class="pp-rubric-criteria-text">
                    Criterion #${this.editorFriendlyInteractions.filter((d) => d.interactionType !== 'pause_q').length + 1}
                  </div>
                  <div class="pp-rubric-criteria-input">
                    <input id="question-0-0" onkeydown="if(event.key==='.'){event.preventDefault();}" class="fill-blank-question" tabindex="-1" type="number" min="0" max="5" placeholder="0 - 5" />
                  </div>
                </div>
              `;
              return html.replace(/\s\s+/g, '');
            }
            return null;
          })(),
          pointsAvailable: type === 'pause_q' ? 0 : 5,
          displayInTime: 0,
          displayOutTime: 100000,
          interactionBank: {
            id: 0,
            order: _.get(this, 'editorFriendlyInteractions.length', 0) + 1,
          },
          id: this.generateUuid(),
        });
        this.$set(this, 'editorFriendlyInteractions', _.orderBy(
          [
            ...this.editorFriendlyInteractions,
            this.constructEditorFriendlyInteraction(template),
          ],
          [(interaction) => _.get(interaction, 'interactionBank.order')],
          ['ASC'],
        ));
        return this.editorFriendlyInteractions;
      } catch(e) {
        return this.$emit('error', e);
      }
    },
    async deleteInteraction(interactionIndex) {
      this.$delete(this.editorFriendlyInteractions, interactionIndex);
      this.applyInteractionBankOrder();
    },
    async saveRubric(action) {
      try {
        this.processingRequest = true;
        const { data: { bank } } = await this.api.applyInteractionBank();
        if (['next', 'exit'].includes(action)) {
          this.$emit(action, bank.interactions);
        } else {
          this.$emit(this.buttonAction === 'next' ? 'next' : 'exit', bank.interactions);
        }
        return bank.interactions;
      } catch(e) {
        return this.$emit('error', e);
      } finally {
        this.processingRequest = false;
      }
    },
    constructEditorFriendlyInteraction(interaction) {
      if (interaction.interactionType === 'pause_q') {
        return {
          interactionText: interaction.interactionText,
          displayInTime: parseFloat(interaction.displayInTime),
          pointsAvailable: 0,
          id: interaction.id,
          interactionType: interaction.interactionType,
          interactionBank: interaction.interactionBank,
        };
      }
      const div = document.createElement('div');
      div.innerHTML = interaction.interactionText;
      const inputElement = div.querySelector('input');
      if (inputElement) inputElement.parentNode.removeChild(inputElement);
      return {
        interactionText: div.textContent,
        displayInTime: interaction.displayInTime,
        pointsAvailable: (() => {
          if (_.isFinite(interaction.pointsAvailable)) return interaction.pointsAvailable;
          else if (_.isString(interaction.pointsAvailable) && interaction.pointsAvailable.length) {
            return parseFloat(interaction.pointsAvailable);
          }
          return 5;
        })(),
        id: interaction.id,
        interactionType: interaction.interactionType,
        interactionBank: interaction.interactionBank,
      };
    },
    initEditorFriendlyInteractions(interactions) {
      this.$set(
        this,
        'editorFriendlyInteractions',
        _(interactions)
          .map(this.constructEditorFriendlyInteraction)
          .orderBy(
            [(interaction) => _.get(interaction, 'interactionBank.order')],
            ['ASC'],
          )
          .value(),
      );
    },
    async getBulbData() {
      try {
        this.loaded = false;
        const { data: bulb } = await this.api.getBulbData(this.bulbId);
        this.$set(this, 'bulb', bulb);
        this.initEditorFriendlyInteractions(bulb.interactions);
        return bulb;
      } catch(e) {
        return this.$emit('error', e);
      } finally {
        this.loaded = true;
      }
    },
  },
  watch: {
    saveToggle() {
      this.saveRubric();
    },
    rubricValidityIssues(n) {
      this.$emit('is-rubric-valid', !n.length);
    },
  },
  mounted() {
    try {
      this.api.init();
      this.getBulbData();
    } catch(e) {
      this.$emit('error', e);
    }
  },
};
</script>

<style lang="scss">
  #playposit-rubric {
    height: 100%;
    min-height: 165px;
    background: white;
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-content: center;

    .br-0 {
      border-radius: 0px !important;
    }

    .text--faded-light {
      color: rgba(255,255,255,0.7);
    }

    .top-nudge {
      position: relative !important;
      top: 7px;
    }

    .min-w-150px {
      min-width: 150px;
    }

    .width-100 {
      width: 100% !important;
    }

    .settings-menu {
      flex: 0;
      position: relative;
      top: -2px;
    }

    &.disabled {
      .drag-handle {
        cursor: default !important;
      }
    }

    .v-form {
      width: 100% !important;
    }

    .rubric-loading-screen {
      position: absolute;
      top: 0;
      right: 0;
      left: 0;
      bottom: 0;
      height: 100%;
      width: 100%;
      background: rgba(255,255,255,0.6);
      pointer-events: none;
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 2;
    }

    .rubric-header {
      color: white !important;

      .total-points {
        font-size: 20px;
      }
    }

    .rubric-body {
      height: 100%;
      max-width: 100% !important;
      overflow-y: auto;
    }

    .v-input--checkbox {
      .v-input__slot {
        display: flex;
        justify-content: center;
      }
    }

    .section {
      &.has-criteria {
        &:before {
          content: '';
          display: block;
          height: calc(100% - 50px);
          width: 2px;
          background: #DDD;
          position: absolute;
          top: 50px;
          left: 11px;
        }
        &:after {
          content: '';
          display: block;
          height: calc(100% - 50px);
          transition: all .3s cubic-bezier(.25,.8,.5,1);
          border: 1px solid;
          border-color: inherit;
          position: absolute;
          top: 50px;
          left: 11px;
          transform: scaleY(0);
        }
      }
      &.is-focused {
        &:after {
          transform: scaleY(1);
        }
      }
    }

    .rubric-criteria {
      padding: 0 16px;
      .rubric-element {
        width: 100%;
        position: relative;

        .drag-handle {
          cursor: grab;
        }

        .side-actions {
          width: 24px;
          overflow: hidden;
          height: 100%;
        }

        &.section {
          .rubric-add-block {
            border: 1px solid #DDD;
            background: white;
          }
          .section-title  {
            border-bottom: 1px solid #ddd;
            margin-bottom: 8px;
            padding-bottom: 2px;
            border-bottom: 0 !important;
          }

          .section-criteria {
            padding-left: 24px;
          }
        }

        &.criteria {
          // &:not(.inside-section) {
          //   .description {
          //     padding-right: 16px;
          //   }
          // }

          .side-actions {
            display: flex;
            flex-direction: column;
            > div {
              flex: 1;
            }
          }
          .description {
            display: flex;
            align-items: center;
          }

          .v-menu {
            flex: 0;
          }
        }
      }

      .title-text {
        padding-left: 12px;
      }

      .rubric-blank {
        padding: 4px 8px;
        width: 100px;
        background: white;
        font-weight: bolder;
        font-size: 12px;
        color: black;
        border: 1px solid;
        cursor: pointer;
        padding: 2px 4px;
      }

      .criteria-settings-block {
        width: 52px;
        height: 48px;
      }
    }

    .rubric-add-block {
      background: #F5F5F5;
      transition: opacity 0.2s ease-in-out;
      position: absolute;
      left: 0;
      bottom: 0;
      right: 0;
      width: 100%;

      &.primary-add-block {
        border-top: 1px solid #DDD;
      }

      &.half-visibility {
        opacity: 0.5;
      }

      &:hover,
      &.full-visibility {
        opacity: 1;
      }

      .v-btn__content {
        color: #757575;
      }

      .v-input__slot,
      .v-input__control {
        &:before,
        &:after {
          display: none;
        }
      }

      .add-criteria-block {
        .v-btn__content {
          padding-left: 32px;
        }
        .criteria-icon {
          position: absolute;
          top: 12px;
          left: 16px;

          &.section {
            font-size: 20px;
            top: 6px;
            left: 20px;
          }
        }
      }

      .add-criteria-button.section {
        &.v-input__control,
        .v-select__slot,
        .v-select__selections,
        button,
        .v-input__append-inner {
          height: 32px !important;
        }
      }

      .v-overflow-btn.v-input--is-focused .v-input__slot {
        box-shadow: none;
      }

      .v-overflow-btn.v-input--is-focused .v-input__slot,
      .v-overflow-btn:hover .v-input__slot {
        background: transparent;
      }

      .theme--light.v-overflow-btn .v-input__control:before, .theme--light.v-overflow-btn .v-input__slot:before {
        display: none;
      }

      .v-text-field.v-input--is-focused .v-input__slot:after {
        display: none;
      }
    }
  }

  .rubric-slider-menu {
    .v-list__tile {
      height: auto !important;
    }

    .v-slider__ticks {
      border-color: transparent !important;

      &:first-child > span {
        transform: translateX(-4px);
      }

      &:last-child > span {
        transform: translateX(-4px);
      }
    }
}
</style>
